import React, { useEffect, useRef } from 'react';
import {
  Flex,
  Gap,
  Icon,
  PlannerCalendar,
  Text,
  type PlannerCalendarColumnType,
  type PlannerCalendarSectionType,
  type PlannerCalendarWeekType,
} from '@common/components';
import type {
  ScheduleDropValidityType,
  ScheduleDayType,
  ScheduleDemandType,
  ScheduleRoleType,
  ScheduleShiftTypeSectionKeyType,
  ScheduleShiftTypeSectionType,
  ScheduleShiftType,
  ScheduleShiftTypeFilterType,
  ScheduleWeekStatusType,
} from '@load-balancing/types';
import { ShiftCalendarCard } from './_shift-calendar-card';
import { ScheduleCalendarControls } from './_schedule-calendar-controls';
import {
  SHIFT_TYPE_SECTION_ACCENT,
  cellDemand,
  cellShifts,
  isCoverageShift,
  scrollShiftCardIntoView,
} from './_schedule-calendar-service';

/**
 * The load-balancing scheduling grid: shift-type sections (rows) × day of week (columns) for one
 * week, with a 6-week navigation strip, role and shift-type filters, and a legend. It maps its
 * (mock, this phase) domain data onto the agnostic `PlannerCalendar` and owns no data-fetching.
 */
export function ScheduleCalendar({
  shifts,
  demands,
  shiftTypeSections,
  roles,
  days,
  weekStatuses,
  activeWeekKey,
  onWeekSelect,
  onPreviousWeeks,
  onNextWeeks,
  roleFilter = [],
  onRoleFilterChange,
  roleStatuses,
  shiftTypeFilter = 'all',
  onShiftTypeFilterChange,
  coverageGapThreshold = 3,
  highlightedShiftId = null,
  availableShiftIds = [],
  assignedShiftIds = [],
  attentionShiftIds = [],
  onShiftClick,
  onWorkerClick,
  dragEnabled = false,
  onShiftDragStart,
  onShiftDragEnd,
  onShiftDrop,
  getDropValidity,
}: {
  shifts: ScheduleShiftType[];
  /** Per-cell planned-demand headcount (the count denominator). */
  demands: ScheduleDemandType[];
  shiftTypeSections: ScheduleShiftTypeSectionType[];
  roles: ScheduleRoleType[];
  days: ScheduleDayType[];
  weekStatuses?: ScheduleWeekStatusType[];
  activeWeekKey?: string;
  onWeekSelect?: (key: string) => void;
  onPreviousWeeks?: () => void;
  onNextWeeks?: () => void;
  roleFilter?: ScheduleRoleType[];
  onRoleFilterChange?: (roles: ScheduleRoleType[]) => void;
  roleStatuses?: Record<ScheduleRoleType, number>;
  shiftTypeFilter?: ScheduleShiftTypeFilterType;
  onShiftTypeFilterChange?: (filter: ScheduleShiftTypeFilterType) => void;
  /** Per-day assigned-vs-total gap (in headcount) at which the Total row turns coral. */
  coverageGapThreshold?: number;
  /** Shift to cross-highlight and scroll into view (e.g. from an activity selection). */
  highlightedShiftId?: string | null;
  /** Open shifts ringed in grass for the highlighted worker (Team tab "Show eligible shifts"). */
  availableShiftIds?: string[];
  /** Shifts filled by the highlighted worker — elm fill (Team tab "Show assigned shifts"). */
  assignedShiftIds?: string[];
  /** Cells ringed in coral by a "Needs Attention" card's "Show on Schedule" (fatigue/unfilled). */
  attentionShiftIds?: string[];
  onShiftClick?: (shiftId: string) => void;
  onWorkerClick?: (shiftId: string) => void;
  /** Enables drag-to-assign across the grid cards. */
  dragEnabled?: boolean;
  /** Drag an assigned worker off their shift (grid reassignment source). */
  onShiftDragStart?: (shiftId: string) => void;
  onShiftDragEnd?: () => void;
  /** Drop the dragged worker onto an open slot. */
  onShiftDrop?: (shiftId: string) => void;
  /** Validity of dropping the in-flight worker onto an open slot, evaluated on hover. */
  getDropValidity?: (shiftId: string) => ScheduleDropValidityType;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const availableShiftIdSet = new Set(availableShiftIds);
  const assignedShiftIdSet = new Set(assignedShiftIds);
  const attentionShiftIdSet = new Set(attentionShiftIds);

  // Center the highlighted card in the grid's scroll container. Deferred one frame:
  // React flushes click-driven effects before paint, and a smooth scroll issued
  // pre-paint is applied instantly instead of animating.
  useEffect(() => {
    if (!highlightedShiftId) return undefined;

    const frame = requestAnimationFrame(() =>
      scrollShiftCardIntoView(rootRef.current, highlightedShiftId)
    );

    return () => cancelAnimationFrame(frame);
    // The week switch lands in the same commit as the selection, so the card is
    // already rendered; depending only on the id avoids re-centering on manual
    // week navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedShiftId]);

  // Center the first available shift rendered in the active week when a worker's
  // availability is toggled on (the page snaps to that shift's week in the same commit).
  const availableShiftKey = availableShiftIds.join('|');
  useEffect(() => {
    const firstInWeek = availableShiftIds.find((id) =>
      shifts.some((shift) => shift.id === id)
    );
    if (!firstInWeek) return undefined;

    const frame = requestAnimationFrame(() =>
      scrollShiftCardIntoView(rootRef.current, firstInWeek)
    );

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableShiftKey]);

  // Center the first "Needs Attention" cell rendered in the active week when a card's
  // "Show on Schedule" sets the targets (the page snaps to that cell's week in the same commit).
  const attentionShiftKey = attentionShiftIds.join('|');
  useEffect(() => {
    const firstInWeek = attentionShiftIds.find((id) =>
      shifts.some((shift) => shift.id === id)
    );
    if (!firstInWeek) return undefined;

    const frame = requestAnimationFrame(() =>
      scrollShiftCardIntoView(rootRef.current, firstInWeek)
    );

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attentionShiftKey]);

  // Center the first assigned shift rendered in the active week when a worker's
  // assigned-shift highlight is toggled on (the page snaps to that shift's week in the same commit).
  const assignedShiftKey = assignedShiftIds.join('|');
  useEffect(() => {
    const firstInWeek = assignedShiftIds.find((id) =>
      shifts.some((shift) => shift.id === id)
    );
    if (!firstInWeek) return undefined;

    const frame = requestAnimationFrame(() =>
      scrollShiftCardIntoView(rootRef.current, firstInWeek)
    );

    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedShiftKey]);

  const visibleRoles = roleFilter.length > 0 ? roleFilter : roles;
  const visibleSections = shiftTypeSections.filter(
    (section) => shiftTypeFilter === 'all' || section.key === shiftTypeFilter
  );
  // Total row hides only when exactly one role is filtered (combined totals still make sense for 2+).
  const showTotals = roleFilter.length !== 1;

  const columns: PlannerCalendarColumnType[] = days.map((day) => ({
    key: day.key,
    header: (
      <Flex vertical align="center">
        <Text size="sm" secondary transform="uppercase">
          {day.dayLabel}
        </Text>
        <Text size="md" bold block>
          {day.dateLabel}
        </Text>
      </Flex>
    ),
  }));

  const renderFooterCell = (
    section: ScheduleShiftTypeSectionKeyType,
    dayKey: string
  ): React.ReactNode => {
    const dayShifts = visibleRoles.flatMap((role) =>
      cellShifts(shifts, section, role, dayKey)
    );
    // Planned demand summed across the visible roles is the denominator; the
    // numerator counts only on-floor coverage (excludes PTO/leave/absence/standby
    // blocks) so the total matches the sum of its cells.
    const planned = visibleRoles.reduce(
      (sum, role) => sum + cellDemand(demands, section, role, dayKey),
      0
    );
    const assigned = dayShifts.filter(isCoverageShift).length;
    const offTarget = Math.abs(planned - assigned) >= coverageGapThreshold;

    return (
      <Text
        size="md"
        weight={offTarget ? 'bold' : 'medium'}
        color={offTarget ? 'coralDarker' : 'midnightLighter'}
      >
        {assigned} / {planned}
      </Text>
    );
  };

  const sections: PlannerCalendarSectionType[] = visibleSections.map(
    (section) => ({
      key: section.key,
      accentColor: SHIFT_TYPE_SECTION_ACCENT[section.key],
      title: (
        <Gap size="xs" align="center">
          <Text size="md" bold>
            {section.label}
          </Text>
          <Icon
            icon={section.icon}
            kind="solid"
            size="sm"
            color={SHIFT_TYPE_SECTION_ACCENT[section.key]}
          />
          <Text size="md" secondary>
            · {section.time}
          </Text>
        </Gap>
      ),
      rows: visibleRoles.map((role) => ({
        key: `${section.key}-${role}`,
        title: (
          <Text size="md" weight="bolder">
            {role}
          </Text>
        ),
        cells: days.map((day) => {
          const dayShifts = cellShifts(shifts, section.key, role, day.key);
          const planned = cellDemand(demands, section.key, role, day.key);
          const assigned = dayShifts.filter(isCoverageShift).length;
          const offTarget =
            Math.abs(planned - assigned) >= coverageGapThreshold;

          return (
            <Gap key={day.key} vertical size="sm" align="stretch">
              <Text
                size="lg"
                weight={offTarget ? 'bold' : 'medium'}
                color={offTarget ? 'coral' : 'textDefault'}
                block
              >
                {assigned} / {planned}
              </Text>
              {dayShifts.length > 0 ? (
                <Gap vertical>
                  {dayShifts.map((shift) => (
                    <ShiftCalendarCard
                      key={shift.id}
                      shift={shift}
                      timeLabel={section.time}
                      highlighted={shift.id === highlightedShiftId}
                      available={availableShiftIdSet.has(shift.id)}
                      assigned={assignedShiftIdSet.has(shift.id)}
                      dragEnabled={dragEnabled}
                      attention={attentionShiftIdSet.has(shift.id)}
                      onShiftClick={onShiftClick}
                      onWorkerClick={onWorkerClick}
                      onShiftDragStart={onShiftDragStart}
                      onShiftDragEnd={onShiftDragEnd}
                      onShiftDrop={onShiftDrop}
                      getDropValidity={getDropValidity}
                    />
                  ))}
                </Gap>
              ) : null}
            </Gap>
          );
        }),
      })),
      footer: showTotals
        ? {
            title: 'Total',
            cells: days.map((day) => renderFooterCell(section.key, day.key)),
            cellBackgrounds: days.map((day) => {
              const planned = visibleRoles.reduce(
                (sum, role) =>
                  sum + cellDemand(demands, section.key, role, day.key),
                0
              );
              const assigned = visibleRoles
                .flatMap((role) =>
                  cellShifts(shifts, section.key, role, day.key)
                )
                .filter(isCoverageShift).length;
              return Math.abs(planned - assigned) >= coverageGapThreshold
                ? 'coralHyperlight'
                : undefined;
            }),
          }
        : undefined,
    })
  );

  const weeks: PlannerCalendarWeekType[] | undefined = weekStatuses?.map(
    (status) => ({
      key: status.key,
      label: status.label,
      indicator: weekIndicator(status),
      alert: status.hasCoverageGap,
    })
  );

  return (
    // display: contents — the ref only anchors the highlight-scroll lookup
    // without disturbing the SideShell flex layout PlannerCalendar fills.
    <div ref={rootRef} style={{ display: 'contents' }}>
      <PlannerCalendar
        className="schedule-calendar"
        columns={columns}
        sections={sections}
        weeks={weeks}
        selectedWeekKey={activeWeekKey}
        onWeekSelect={onWeekSelect}
        onPreviousWeeks={onPreviousWeeks}
        onNextWeeks={onNextWeeks}
        header={
          <ScheduleCalendarControls
            roles={roles}
            roleFilter={roleFilter}
            onRoleFilterChange={onRoleFilterChange}
            roleStatuses={roleStatuses}
            shiftTypeSections={shiftTypeSections}
            shiftTypeFilter={shiftTypeFilter}
            onShiftTypeFilterChange={onShiftTypeFilterChange}
          />
        }
      />
    </div>
  );
}

function weekIndicator(_status: ScheduleWeekStatusType): React.ReactNode {
  return undefined;
}
